import type { Recording } from '@/lib/protobuf/gen/recordings/recordings_pb';
import { transcriptSnippet } from './transcriptSnippet';

/** Trim + lowercase a raw query for case-insensitive substring matching. */
export function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * The full searchable text of a recording: title, original filename, speaker
 * names, and the transcript (flat text + every segment). Lowercased once so
 * `recordingMatches` is a plain `includes`. The transcript is already embedded
 * in each list row (see `recordingToProto`), so this is free client-side.
 */
function haystack(rec: Recording): string {
  const parts: string[] = [rec.title, rec.originalName, ...rec.speakers];
  const t = rec.transcript;
  if (t) {
    if (t.text) parts.push(t.text);
    for (const seg of t.segments) parts.push(seg.text);
  }
  return parts.join(' ').toLowerCase();
}

/** True when the (already-normalized, non-empty) query appears in the recording. */
export function recordingMatches(rec: Recording, normalized: string): boolean {
  return haystack(rec).includes(normalized);
}

/**
 * Filter a list by a raw query. An empty/whitespace query returns the list
 * unchanged (same reference) so callers can cheaply detect "no filter".
 */
export function filterRecordings(list: Recording[], rawQuery: string): Recording[] {
  const q = normalizeQuery(rawQuery);
  if (!q) return list;
  return list.filter((rec) => recordingMatches(rec, q));
}

/**
 * A transcript snippet biased toward the search match: when the query appears
 * in the transcript, return a window centred on the first hit (with leading /
 * trailing ellipses), so the card shows *why* the row matched rather than the
 * generic opening. Falls back to the plain leading snippet when the query is
 * empty or only matched the title/speaker/filename.
 */
export function transcriptExcerpt(rec: Recording, rawQuery: string, maxChars = 160): string {
  const q = normalizeQuery(rawQuery);
  const t = rec.transcript;
  if (!q || !t) return transcriptSnippet(rec, maxChars);

  const full = (t.segments.map((s) => s.text).join(' ') || t.text || '')
    .replace(/\s+/g, ' ')
    .trim();
  const idx = full.toLowerCase().indexOf(q);
  if (idx === -1) return transcriptSnippet(rec, maxChars);
  if (full.length <= maxChars) return full;

  // Window centred on the match, snapped to word boundaries, with ellipses
  // marking any trimmed side.
  const pad = Math.max(0, Math.floor((maxChars - q.length) / 2));
  let start = Math.max(0, idx - pad);
  let end = Math.min(full.length, idx + q.length + pad);
  if (start > 0) {
    const sp = full.indexOf(' ', start);
    if (sp !== -1 && sp < idx) start = sp + 1;
  }
  if (end < full.length) {
    const sp = full.lastIndexOf(' ', end);
    if (sp > idx + q.length) end = sp;
  }
  const core = full.slice(start, end).trim();
  return `${start > 0 ? '…' : ''}${core}${end < full.length ? '…' : ''}`;
}
